<template>
    <div class="kv-filter-container">
        <div class="kv-filter-label">{{ label }}</div>

        <KvPopup max-width="400px" min-width="400px" ref="popup">
            <template #trigger="{ toggle }">
                <span class="kv-filter-input" :title="valueLabel" @click="toggle">
                    <Icon :icon-class="triggerIcon" size="2" />
                    {{ valueLabel }}
                    <span class="kv-remove-filter" v-if="removable" @click.stop="$emit('remove')">
                        <Icon icon-class="ui-x-close" size="2" color="#212529" />
                    </span>
                </span>
            </template>

            <template #content="{ toggle }">
                <div class="kv-checkbox-options">
                    <KvInput v-model="searchQuery" :placeholder="t('reportFilters-search_placeholder')">
                        <template #prepend>
                            <Icon icon-class="ui-search-01" size="2" />
                        </template>
                    </KvInput>

                    <div class="kv-options">
                        <span
                            class="kv-checkbox"
                            v-for="(option, index) in filteredOptions"
                            :key="index"
                            @click="toggleOption(option.value)"
                        >
                            <KvCheckbox
                                :id="`filter-${option.value}-${index}`"
                                :checked="isChecked(option.value)"
                                label=""
                            />
                            <span
                                v-if="option.color"
                                class="option-color-dot"
                                :style="{ backgroundColor: option.color }"
                            ></span>
                            <span class="option-label">{{ option.label }}</span>
                        </span>
                    </div>

                    <div class="kv-popup-actions">
                        <KvButton full-width variant="outlined" @click="cancel(toggle())">
                            {{ t('reportFilters-cancel') }}
                        </KvButton>
                        <KvButton full-width prepend-icon="ui-check" variant="filled" @click="apply(toggle())">
                            {{ t('reportFilters-apply') }}
                        </KvButton>
                    </div>
                </div>
            </template>
        </KvPopup>
    </div>
</template>


<style lang="less">
.kv-checkbox-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.kv-popup-actions {
    display: flex;
    gap: 10px;
}

.kv-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 6px 8px;
    border-radius: 6px;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #f3f4f6;
    }

    .option-color-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    .option-label {
        font-size: 13px;
        font-weight: 400;
        color: #384250;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>

<script>
import KvButton from "../../form/kv-button.vue";
import Icon from "../../icon/icon";
import KvCheckbox from "../../inputs/kv-checkbox";
import KvInput from "../../inputs/kv-input.vue";
import KvPopup from "../../ui/kv-popup";

export default {
    name: "filter-checkbox-filter",
    components: {
        Icon,
        KvCheckbox,
        KvPopup,
        KvInput,
        KvButton
    },
    props: {
        options: {
            type: [Object, Array],
            default: () => ({}),
        },
        name: {
            type: String,
            default: "filter-checkbox",
        },
        currentValue: {
            type: [Array, String, Number],
            default: null,
        },
        label: {
            type: String,
            default: "",
        },
        removable: Boolean,
    },
    data() {
        return {
            value: [],
            newValue: [],
            searchQuery: "",
            focus: false,
        };
    },
    mounted() {
        if (this.currentValue) {
            this.value = this.currentValue;
            this.newValue = [...this.value];
        }
        if (this.value.length > 0) {
            this.changed();
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        toggleOption(value) {
            let idx = this.newValue.indexOf(String(value));
            if (idx === -1) {
                this.newValue.push(String(value));
            } else {
                this.newValue.splice(idx, 1);
            }
        },
        isChecked(value) {
            return this.newValue.indexOf(String(value)) !== -1;
        },
        apply() {
            this.value = [...this.newValue];
        },
        cancel() {
            this.newValue = [...this.value];
        },
        changed() {
            this.$emit("changed", this.value.length > 0 ? this.value : null);
        },
    },
    computed: {
        triggerIcon() {
            return (this.options && this.options.icon) || "ui-chevron-down";
        },
        valueLabel() {
            if (this.value.length === 0) {
                return this.t('reportFilters-select');
            }
            let label = this.value
                .map((value) => this.optionsMap[value])
                .join(", ");
            if (label.length > 30) {
                label = label.substr(0, 27) + "...";
            }
            return label;
        },
        optionsMap() {
            let optionsMap = {};
            for (let o in this.options.options) {
                let option = this.options.options[o];
                optionsMap[option.value] = option.label;
            }
            return optionsMap;
        },
        filteredOptions() {
            let options = this.options.options;
            if (this.searchQuery) {
                options = options.filter((option) =>
                    option.label.toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            }
            // After applying, show selected (committed) values first
            if (this.value.length > 0) {
                const selected = [];
                const unselected = [];
                options.forEach((option) => {
                    if (this.value.includes(String(option.value))) {
                        selected.push(option);
                    } else {
                        unselected.push(option);
                    }
                });
                return [...selected, ...unselected];
            }
            return options;
        },
    },
    watch: {
        value() {
            this.changed();
        },
        focus(newValue) {
            this.$refs.popup.toggleShow(newValue);
        }
    },
};
</script>
